/**
 * Session length without "Remember me for 30 days": stay signed in while the
 * person is using the portal, sign out after 30 minutes of inactivity or
 * 30 minutes away from the site. The last activity time is kept in
 * localStorage so it survives reloads and is shared across tabs; the login
 * token itself (24 h) is the hard ceiling.
 *
 * With "Remember me" the timer is off: the 30-day token is the only limit.
 */
import { logout } from './authUtils';

export const IDLE_MS = 30 * 60 * 1000;
const LAST_KEY = 'wk_last_activity';
const REMEMBER_KEY = 'wk_remember_me';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointermove'];

const safe = (fn) => { try { return fn(); } catch (e) { return undefined; } };

export const setRememberMe = (on) => safe(() => localStorage.setItem(REMEMBER_KEY, on ? '1' : '0'));
export const rememberMeOn = () => safe(() => localStorage.getItem(REMEMBER_KEY)) === '1';
export const touchActivity = () => safe(() => localStorage.setItem(LAST_KEY, String(Date.now())));
const lastActivity = () => Number(safe(() => localStorage.getItem(LAST_KEY))) || 0;

/** True when the session should end now (no remember-me and idle too long). */
export const idleExpired = () => {
  if (rememberMeOn()) return false;
  if (!safe(() => localStorage.getItem('token'))) return false;
  const last = lastActivity();
  return last > 0 && Date.now() - last > IDLE_MS;
};

const expireNow = () => {
  safe(() => sessionStorage.setItem('wk_logout_reason', 'idle'));
  logout();
};

/** Start tracking; returns a stop function. Safe to call once at app start. */
export function startIdleLogout() {
  if (idleExpired()) { expireNow(); return () => {}; }
  if (safe(() => localStorage.getItem('token')) && !lastActivity()) touchActivity();

  let throttle = 0;
  const onActivity = () => {
    const now = Date.now();
    if (now - throttle < 15000) return; // write at most every 15 s
    throttle = now;
    if (safe(() => localStorage.getItem('token'))) touchActivity();
  };
  const check = () => { if (idleExpired()) expireNow(); };
  const onVisible = () => { if (document.visibilityState === 'visible') check(); };

  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(check, 60 * 1000);
  return () => {
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}
